import { useMemo } from 'react';
import type { AccommodationListingDTO, MapViewPort } from '@shared/types/Accommodations.types';
import { useListingSortingByViewport } from './useListingSortingByViewport';

type UseViewportListingsArgs = Readonly<{
  listings: ReadonlyArray<AccommodationListingDTO>;
  mapViewPort: Maybe<MapViewPort>;
  /** Only true once the guest has deliberately moved the map on a layout that shows it. */
  isViewportFilterActive: boolean;
}>;

type UseViewportListingsReturn = Readonly<{
  listings: ReadonlyArray<AccommodationListingDTO>;
  /** The viewport held nothing, so we fell back to every result and owe the guest an explanation. */
  isShowingOutsideViewport: boolean;
}>;

/**
 * Narrows the results to the hotels inside the map's bounds, with two guards the raw filter lacks.
 *
 * 1. It only narrows after a real pan or zoom. On first load and on a fresh search the bounds are
 *    ours, not the guest's, so filtering by them can hide results nobody chose to hide.
 * 2. It never returns nothing. An empty viewport falls back to the full list, because a stale or
 *    mis-fitted viewport used to leave the page permanently empty: no markers meant no auto-fit,
 *    so the bounds could never recover on their own.
 */
export const useViewportListings = ({ listings, mapViewPort, isViewportFilterActive }: UseViewportListingsArgs): UseViewportListingsReturn => {
  const sortListingsInViewport = useListingSortingByViewport();

  return useMemo(() => {
    if (!isViewportFilterActive) return { listings, isShowingOutsideViewport: false };

    const listingsInViewport = sortListingsInViewport(mapViewPort, listings);
    if (listingsInViewport.length === 0 && listings.length > 0) {
      return { listings, isShowingOutsideViewport: true };
    }

    return { listings: listingsInViewport, isShowingOutsideViewport: false };
  }, [listings, mapViewPort, isViewportFilterActive, sortListingsInViewport]);
};
